import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { Button, Dropdown, Label } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import { useShallow } from 'zustand/react/shallow';
import { useDataStore } from '@/stores/dataStore';
import { pickKeys } from '@/lib/pickKeys';
import { api } from '@/lib/api';
import { useGuildRoster } from '@/lib/useGuildRoster';
import { PERMISSIONS, useMyGuildPermissions } from '@/lib/guildPermissions';
import { errToToast } from '@/lib/humanizeApiError';
import {
  HashIcon,
  VolumeIcon,
  PlusIcon,
  EditIcon,
  ChevronDownIcon,
} from '@/components/icons';
import { emitAppEvent } from '@/lib/appEvents';
import { UnreadBadge } from '@/components/ui/UnreadBadge';
import { computeChannelReorder } from './channelReorder';
import { joinVoice } from '@/lib/voiceCommands';
import { askBroadcastConsent } from '@/components/BroadcastConsentModal';
import { loadCollapsed, saveCollapsed } from './channelList/collapsedCategories';
import { VoiceCallTimer, VoiceParticipant } from './channelList/voiceRowParts';
import type { Channel, Guild } from '@/types/api';

type DragState = {
  draggingId: string | null;
  overId: string | null;
  overPosition: 'before' | 'after';
};

// Collapsed-category ids, persisted per-device so a folded category stays
// folded across reloads (Discord behaviour). Stored as a flat id array.
const byPosition = (a: Channel, b: Channel) => a.position - b.position;

/**
 * Guild channel list for the HeroUI sidebar. Channels render as buttons with
 * a sliding Framer Motion active highlight (layoutId). Server managers get a
 * "+" Dropdown (text / voice / category) and a per-channel edit pencil, both
 * wired to the shared ChannelModal.
 */
export function HeroChannelList({ guild, onNavigate }: { guild: Guild; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const currentChannelId = useDataStore((s) => s.currentChannelId);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const myVoiceChannelId = useDataStore((s) => s.myVoiceChannelId);
  // This guild's channels only: voice, unread and mentions elsewhere (other
  // guilds, DMs) must not re-render the list.
  const channelIds = useMemo(() => guild.channels.map((c) => c.id), [guild.channels]);
  const voiceByChannel = useDataStore(useShallow((s) => pickKeys(s.voiceByChannel, channelIds)));
  const unreadByChannel = useDataStore(useShallow((s) => pickKeys(s.unreadByChannel, channelIds)));
  const mentionByChannel = useDataStore(useShallow((s) => pickKeys(s.mentionByChannel, channelIds)));
  const setGuildChannels = useDataStore((s) => s.setGuildChannels);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed());

  function toggleCollapse(categoryId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      saveCollapsed(next);
      return next;
    });
  }

  // Native HTML5 drag-and-drop reorder (same approach as the roles list).
  // `draggingIdRef` mirrors state so dragOver — which fires rapidly — can read
  // the active id without waiting for a render.
  const draggingIdRef = useRef<string | null>(null);
  const [drag, setDrag] = useState<DragState>({
    draggingId: null,
    overId: null,
    overPosition: 'before',
  });

  function applyReorder(draggingId: string, targetId: string, pos: 'before' | 'after') {
    const orders = computeChannelReorder(guild.channels, draggingId, targetId, pos);
    if (!orders) return;
    // Optimistic, in one store update: the list resettles at once and the
    // server's `guild:channels` reconciles. A refusal puts the old order back.
    const previous = guild.channels;
    const byId = new Map(orders.map((o) => [o.id, o]));
    setGuildChannels(
      guild.id,
      previous.map((ch) => {
        const o = byId.get(ch.id);
        return o ? { ...ch, position: o.position, ...(o.parentId !== undefined ? { parentId: o.parentId } : {}) } : ch;
      }),
    );
    void api.reorderChannels(guild.id, orders).catch((err) => {
      setGuildChannels(guild.id, previous);
      toast.error(...errToToast(err, t('channels.reorderError')));
    });
  }

  function endDrag() {
    draggingIdRef.current = null;
    setDrag({ draggingId: null, overId: null, overPosition: 'before' });
  }

  // Shared drag handlers for a draggable row (channel or category header).
  function dragProps(id: string) {
    if (!canManage) return {};
    return {
      draggable: true,
      onDragStart: () => {
        draggingIdRef.current = id;
        setDrag({ draggingId: id, overId: null, overPosition: 'before' });
      },
      onDragOver: (e: React.DragEvent) => {
        const current = draggingIdRef.current;
        if (!current || current === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        const before = e.clientY - rect.top < rect.height / 2;
        const nextPos: 'before' | 'after' = before ? 'before' : 'after';
        setDrag((s) =>
          s.overId === id && s.overPosition === nextPos
            ? s
            : { ...s, overId: id, overPosition: nextPos },
        );
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const current = draggingIdRef.current;
        if (current && current !== id) applyReorder(current, id, drag.overPosition);
        endDrag();
      },
      onDragEnd: endDrag,
    };
  }

  // Marker classes for the active drop target (a thin accent line).
  function dropMarker(id: string) {
    const isOver = drag.overId === id;
    return (
      (isOver && drag.overPosition === 'before'
        ? 'before:absolute before:-top-px before:left-0 before:right-0 before:h-0.5 before:rounded before:bg-accent '
        : '') +
      (isOver && drag.overPosition === 'after'
        ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded after:bg-accent '
        : '')
    );
  }

  // Resolve voice participants (which carry only userId) to display names +
  // avatars via the shared, version-aware roster cache, indexed by userId.
  const { members } = useGuildRoster(guild.id);
  const membersById = useMemo(
    () => new Map(members.map((m) => [m.userId, m])),
    [members],
  );

  // Discord-style: clicking a voice channel both opens its view and connects
  // (unless you're already in it). Text channels just navigate. Join errors
  // surface as a toast; the voice-stub view shows live state once connected.
  function openChannel(c: Channel) {
    selectChannel(c.id);
    onNavigate?.();
    if (c.type === 'VOICE' && c.id !== myVoiceChannelId) {
      joinVoice(c.id, {
        onError: (msg) => toast.error(msg),
        onConsentRequired: askBroadcastConsent,
      });
    }
  }

  // Channel management (create/edit/reorder) follows the MANAGE_CHANNELS
  // permission — owner and ADMINISTRATOR short-circuit inside `can`. The
  // server routes already enforce the same bit; gating the sidebar to the
  // owner only hid the controls from legitimate admins («админка не
  // работает» feedback).
  const { can } = useMyGuildPermissions(guild.id);
  const canManage = can(PERMISSIONS.MANAGE_CHANNELS);

  // Group channels in a single pass instead of filtering/sorting per render
  // and once more per category. Categories and orphans come out sorted by
  // position; each category's children are bucketed and sorted together.
  const { categories, orphans, childrenByCategory } = useMemo(() => {
    const cats: Channel[] = [];
    const orph: Channel[] = [];
    const byCat = new Map<string, Channel[]>();
    for (const c of guild.channels) {
      if (c.type === 'CATEGORY') {
        cats.push(c);
      } else if (!c.parentId) {
        orph.push(c);
      } else {
        const bucket = byCat.get(c.parentId);
        if (bucket) bucket.push(c);
        else byCat.set(c.parentId, [c]);
      }
    }
    cats.sort(byPosition);
    orph.sort(byPosition);
    for (const bucket of byCat.values()) bucket.sort(byPosition);
    return { categories: cats, orphans: orph, childrenByCategory: byCat };
  }, [guild.channels]);
  const childrenOf = (catId: string) => childrenByCategory.get(catId) ?? [];

  function createChannel(type: 'TEXT' | 'VOICE' | 'CATEGORY', parentId?: string | null) {
    emitAppEvent('channel:settings', { kind: 'create', guildId: guild.id, type, parentId: parentId ?? null });
  }

  const renderItem = (c: Channel, categoryColor?: string | null) => {
    const active = c.id === currentChannelId;
    // Text channels carry unread; voice/category never do. An unread channel
    // brightens its name (Discord cue) and shows a dot / mention pill.
    const unread = c.type === 'VOICE' || c.type === 'CATEGORY' ? 0 : unreadByChannel[c.id] ?? 0;
    const mentions = c.type === 'VOICE' || c.type === 'CATEGORY' ? 0 : mentionByChannel[c.id] ?? 0;
    const hasUnread = unread > 0 || mentions > 0;
    const voiceMembers = c.type === 'VOICE' ? voiceByChannel[c.id] ?? [] : [];
    // Server-authoritative call start = earliest participant's joinedAt, so the
    // timer is identical across clients and survives a refresh.
    const callStartedAt =
      voiceMembers.length > 0
        ? Math.min(...voiceMembers.map((v) => v.joinedAt).filter((t) => t > 0))
        : 0;
    const dragging = drag.draggingId === c.id;
    return (
      <div key={c.id} data-tour={c.type === 'VOICE' ? 'voice-channel' : undefined} className="flex flex-col">{/* row + voice participants */}
      <div
        {...dragProps(c.id)}
        className={
          'group/ch relative flex items-center ' +
          (dragging ? 'opacity-40 ' : '') +
          dropMarker(c.id)
        }
      >
        {/* role="button" (not <button>): a real <button> is a form control that
            swallows the parent's native drag-start in Chromium, so the row
            couldn't be dragged. A div initiates the draggable ancestor's drag
            from anywhere on the row. */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => openChannel(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openChannel(c);
            }
          }}
          aria-current={active ? 'true' : undefined}
          className={
            'studio-channel relative flex min-w-0 flex-1 cursor-pointer select-none items-center rounded-lg px-2.5 py-[7px] text-left text-[14px] tracking-[-0.006em] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/50 ' +
            // Touch: only the open channel shows its pencil (room for it).
            (canManage && active ? '[@media(pointer:coarse)]:pr-12 ' : '') +
            (active
              ? 'text-foreground'
              : (hasUnread ? 'text-foreground' : 'text-muted') + ' elev-hover hover:text-foreground')
          }
        >
          {active && (
            <motion.span
              layoutId="hero-active-channel"
              className="absolute inset-0 rounded-lg bg-accent/[0.08]"
              transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
            />
          )}
          <span className="relative flex min-w-0 items-center gap-2">
            {c.type === 'VOICE' ? (
              // Speaker icon goes green while the channel has anyone in it.
              <VolumeIcon
                className={
                  'size-[18px] shrink-0 ' +
                  (voiceMembers.length > 0 ? 'text-success' : 'text-muted')
                }
              />
            ) : (
              <HashIcon className="size-[18px] shrink-0 text-muted" />
            )}
            <span
              className={
                'min-w-0 flex-1 truncate ' + (active || hasUnread ? 'font-medium' : '')
              }
              // Category accent: the parent category's colour tints its channel
              // names — including the active one (reads fine on the highlight).
              style={categoryColor ? { color: categoryColor } : undefined}
            >
              {c.name ?? t('channels.fallbackName')}
            </span>
            {hasUnread && (
              <UnreadBadge unread={unread} mentions={mentions} className="shrink-0" />
            )}
            {/* Live green call timer while the voice channel is occupied. */}
            {voiceMembers.length > 0 && callStartedAt > 0 && (
              <VoiceCallTimer
                startedAt={callStartedAt}
                className="ml-auto shrink-0 pl-1 font-mono text-xs tabular-nums text-success"
              />
            )}
          </span>
        </div>
        {canManage && (
          <Tooltip label={t('channels.settings')}>
          <button
            type="button"
            aria-label={t('channels.settings')}
            onClick={() => emitAppEvent('channel:settings', { kind: 'edit', channel: c })}
            className={'absolute right-1 grid size-6 place-items-center rounded-lg text-muted opacity-0 transition hover:bg-default/60 hover:text-foreground focus-visible:opacity-100 group-hover/ch:opacity-100 group-focus-within/ch:opacity-100 [@media(pointer:coarse)]:right-0 [@media(pointer:coarse)]:top-1/2 [@media(pointer:coarse)]:size-11 [@media(pointer:coarse)]:-translate-y-1/2 ' +
              // Touch: only the open channel shows its pencil. Others stay in
              // the tab order (keyboard) but are invisible and untappable until
              // focused from the keyboard.
              (active
                ? '[@media(pointer:coarse)]:opacity-100'
                : '[@media(pointer:coarse)]:pointer-events-none [@media(pointer:coarse)]:opacity-0 [@media(pointer:coarse)]:focus-visible:opacity-100')}
          >
            <EditIcon className="size-3.5" />
          </button>
          </Tooltip>
        )}
      </div>
      {voiceMembers.length > 0 && (
        <ul className="mb-0.5 ml-3 flex flex-col gap-0.5 border-l border-separator pl-3 pt-0.5">
          {voiceMembers.map((v) => (
            <VoiceParticipant
              key={v.userId}
              state={v}
              member={membersById.get(v.userId) ?? null}
              guildId={guild.id}
              channelId={c.id}
            />
          ))}
        </ul>
      )}
      </div>
    );
  };

  return (
    <>
      <nav aria-label={t('channels.title')} className="flex flex-col gap-0.5">
        {canManage && (
          <div className="px-1 py-1">
            <Dropdown>
              <Button data-tour="create-channel-btn" size="sm" variant="ghost" className="studio-create-channel flex w-full items-center justify-between rounded-lg px-2.5 text-left">
                <span className="studio-create-label text-xs font-medium text-muted">{t('channels.create')}</span>
                <PlusIcon className="size-4 shrink-0" />
              </Button>
              <Dropdown.Popover>
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === 'text') createChannel('TEXT');
                    else if (key === 'voice') createChannel('VOICE');
                    else if (key === 'category') createChannel('CATEGORY');
                  }}
                >
                  <Dropdown.Item id="text" textValue={t('channels.text')}>
                    <Label>{t('channels.text')}</Label>
                  </Dropdown.Item>
                  <Dropdown.Item id="voice" textValue={t('channels.voice')}>
                    <Label>{t('channels.voice')}</Label>
                  </Dropdown.Item>
                  <Dropdown.Item id="category" textValue={t('channels.category')}>
                    <Label>{t('channels.category')}</Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </div>
        )}
        {/* Uncategorised channels sit flush at the top — no category chrome. */}
        {orphans.map((c) => renderItem(c))}

        {categories.map((cat) => {
          const isCollapsed = collapsed.has(cat.id);
          const children = childrenOf(cat.id);
          // Even when collapsed, keep the active channel and any voice channel
          // with people in it visible so the fold never hides live activity.
          const shown = isCollapsed
            ? children.filter(
                (c) =>
                  c.id === currentChannelId ||
                  (c.type === 'VOICE' && (voiceByChannel[c.id]?.length ?? 0) > 0),
              )
            : children;
          return (
            <section key={cat.id} className="mt-3">
              <div
                {...dragProps(cat.id)}
                className={
                  'group/cat relative flex items-center gap-1 ' +
                  (drag.draggingId === cat.id ? 'opacity-40 ' : '') +
                  dropMarker(cat.id)
                }
              >
                {/* Chevron + name = the whole header is the collapse toggle.
                    div role=button (not <button>) so it doesn't swallow the
                    draggable ancestor's drag-start (form controls do). */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCollapse(cat.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCollapse(cat.id);
                    }
                  }}
                  aria-expanded={!isCollapsed}
                  className="studio-category-toggle flex min-w-0 flex-1 cursor-pointer select-none items-center gap-1 rounded px-1.5 py-1 text-left text-muted outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  <ChevronDownIcon
                    className={
                      'size-3.5 shrink-0 transition-transform ' + (isCollapsed ? '-rotate-90' : '')
                    }
                  />
                  <span
                    className="studio-category-title truncate text-xs font-semibold uppercase tracking-[0.08em]"
                    // The category header itself also takes its colour.
                    style={cat.color ? { color: cat.color } : undefined}
                  >
                    {cat.name}
                  </span>
                </div>
                {canManage && (
                  <Tooltip label={t('channels.categorySettingsOf', { name: cat.name })}>
                  <button
                    type="button"
                    aria-label={t('channels.categorySettingsOf', { name: cat.name })}
                    onClick={() => emitAppEvent('channel:settings', { kind: 'edit', channel: cat })}
                    className="studio-category-action grid size-5 shrink-0 place-items-center rounded text-muted opacity-0 transition hover:text-foreground focus-visible:opacity-100 group-hover/cat:opacity-100 group-focus-within/cat:opacity-100 [@media(pointer:coarse)]:opacity-100"
                  >
                    <EditIcon className="size-3.5" />
                  </button>
                  </Tooltip>
                )}
                {canManage && (
                  <Dropdown>
                    <Tooltip label={t('channels.createInCategory', { name: cat.name })}>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="ghost"
                      aria-label={t('channels.createInCategory', { name: cat.name })}
                      className="studio-category-action size-5 min-w-0 shrink-0"
                    >
                      <PlusIcon className="size-3.5" />
                    </Button>
                    </Tooltip>
                    <Dropdown.Popover>
                      <Dropdown.Menu
                        onAction={(key) => {
                          if (key === 'text') createChannel('TEXT', cat.id);
                          else if (key === 'voice') createChannel('VOICE', cat.id);
                        }}
                      >
                        <Dropdown.Item id="text" textValue={t('channels.text')}>
                          <Label>{t('channels.text')}</Label>
                        </Dropdown.Item>
                        <Dropdown.Item id="voice" textValue={t('channels.voice')}>
                          <Label>{t('channels.voice')}</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                )}
              </div>
              {/* Children nest under a faint left guide so the grouping reads
                  at a glance — clearly distinct from top-level channels. */}
              {shown.length > 0 && (
                <div className="ml-2.5 mt-0.5 flex flex-col gap-0.5  pl-2">
                  {shown.map((c) => renderItem(c, cat.color))}
                </div>
              )}
            </section>
          );
        })}
      </nav>
    </>
  );
}
